/**
 * Plan display helpers + workspace switching, shared by Sidebar, Layout,
 * Billing and Workspaces pages.
 */

import { authApi, getWorkspaceId, setWorkspaceId } from './api';
import type { BillingInfo } from './api';

export const PLAN_NAMES: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  agency: 'Agency',
};

export type PlanStatusTone = 'success' | 'warning' | 'danger' | 'accent' | 'neutral';

export interface PlanStatus {
  key: 'trial' | 'active' | 'past_due' | 'canceled' | 'free' | 'comped';
  label: string;
  tone: PlanStatusTone;
  /** Show an Upgrade call-to-action (trial or free). */
  upsell: boolean;
}

export function getPlanStatus(b: BillingInfo): PlanStatus {
  if (b.source === 'trial') {
    const days = b.trialDaysLeft ?? 0;
    return {
      key: 'trial',
      label: days > 0 ? `Trial · ${days} day${days === 1 ? '' : 's'} left` : 'Trial ends today',
      tone: days <= 3 ? 'warning' : 'accent',
      upsell: true,
    };
  }
  if (b.plan === 'free') {
    const canceled = b.subscription && ['canceled', 'expired', 'completed'].includes(b.subscription.status);
    return { key: canceled ? 'canceled' : 'free', label: canceled ? 'Canceled · Free plan' : 'Free plan', tone: 'neutral', upsell: true };
  }
  const status = b.subscription?.status;
  if (status === 'past_due') return { key: 'past_due', label: 'Payment past due', tone: 'danger', upsell: false };
  if (b.subscription?.cancelAtPeriodEnd) return { key: 'canceled', label: 'Cancels at period end', tone: 'warning', upsell: false };
  if (!b.subscription) return { key: 'comped', label: 'Active', tone: 'success', upsell: false };
  return { key: 'active', label: 'Active', tone: 'success', upsell: false };
}

/**
 * Switch into a workspace (or back to the agency with null), then do a full
 * reload so every page refetches as the new account. The selection is the
 * per-tab X-MLS-Workspace header; it is validated with GET /api/users/me
 * before reloading. Returns an error message on failure; on success the page
 * navigates away.
 */
export async function switchWorkspaceAndReload(workspaceId: string | null, to?: string): Promise<string | null> {
  const previous = getWorkspaceId();
  setWorkspaceId(workspaceId);
  if (workspaceId) {
    const res = await authApi.getMe();
    if (!res.success || res.data?.workspace?.id !== workspaceId) {
      setWorkspaceId(previous);
      return res.message || 'Could not switch workspace';
    }
  }
  // Detail routes (e.g. /vault/snapshots/:id/browse) belong to the old account.
  const path = to ?? (window.location.pathname.split('/').filter(Boolean).length > 2 ? '/dashboard' : window.location.pathname);
  window.location.assign(path || '/dashboard');
  return null;
}
